import { useState } from 'react';

import { ChevronDown, CirclePlus } from 'lucide-react';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@nesso/ui/primitives/command';
import { Field, FieldSlot } from '@nesso/ui/primitives/field';
import { inputInvalidClassName } from '@nesso/ui/primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { selectTriggerClassName } from '@nesso/ui/primitives/select';

import { useSheetStore } from '@/shared/stores/sheet-store';

import type { ScheduleSessionClient } from '../../../model/schedule-session-form.schema';
import { useScheduleSessionClientSearch } from '../../../model/use-schedule-session-client-search';

type Props = {
    value: ScheduleSessionClient | null;
    error?: boolean | string;
    onSelect: (client: ScheduleSessionClient) => void;
};

// Popover + Command, not Combobox — same reason as `SaveServiceCodePicker`: the design puts a
// search panel under a select-style trigger, with an action row `Combobox` has no slot for.
export function ScheduleSessionClientPicker({ value, error, onSelect }: Props) {
    const [open, setOpen] = useState(false);
    const { search, setSearch, clients, hasNoClients, hasNoResults } =
        useScheduleSessionClientSearch(open);
    const openSheet = useSheetStore((state) => state.openSheet);
    const wiring = useFieldWiring({ error });

    function selectClient(client: ScheduleSessionClient) {
        onSelect(client);
        setSearch('');
        setOpen(false);
    }

    function addNewClient() {
        openSheet({
            type: 'clients/add',
            label: 'New Client',
            data: { prefillName: search, onCreated: selectClient },
        });
        setSearch('');
        setOpen(false);
    }

    return (
        <Field data-invalid={wiring.dataInvalid}>
            {/* modal: gives the portaled list its own scroll lock; the dialog's lock blocks wheel and touch. */}
            <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger
                    {...wiring.controlProps}
                    type="button"
                    className={cn(
                        selectTriggerClassName,
                        inputInvalidClassName,
                        'bg-input-background justify-between',
                    )}
                >
                    <span className="truncate">{value?.name ?? 'Select client'}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent
                    className="w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width) p-0"
                    align="start"
                    collisionPadding={24}
                >
                    <Command shouldFilter={false}>
                        {!hasNoClients && (
                            <CommandInput
                                value={search}
                                onValueChange={setSearch}
                                placeholder="Search"
                            />
                        )}
                        <CommandList>
                            {!hasNoClients && (
                                <>
                                    {/* `shouldFilter={false}` because the search already happened
                                    server-side. cmdk's `CommandEmpty` never shows since "Add new
                                    client" always counts as a registered item — hence the plain div
                                    below. */}
                                    {clients.length > 0 ? (
                                        <CommandGroup>
                                            {clients.map((client) => (
                                                <CommandItem
                                                    key={client.id}
                                                    value={client.id}
                                                    onSelect={() =>
                                                        selectClient({
                                                            id: client.id,
                                                            name: client.name,
                                                        })
                                                    }
                                                    // Overrides the primitive's default 44px row height.
                                                    className="py-1.5"
                                                >
                                                    {client.name}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    ) : hasNoResults ? (
                                        <div className="py-6 text-center text-sm">
                                            No clients found.
                                        </div>
                                    ) : null}
                                    <CommandSeparator />
                                </>
                            )}
                            <CommandGroup>
                                <CommandItem
                                    value="add-new-client"
                                    onSelect={addNewClient}
                                    className="py-1.5"
                                >
                                    <CirclePlus className="h-4 w-4" />
                                    Add new client
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
